import { useEffect, useState } from 'react';
import {
  Box, Grid, Stack, Typography, Card, CardActionArea, Alert,
  Checkbox, Radio, Divider, Chip, Collapse, IconButton, Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useGetWmsWarehouseQuery, useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';
import {
  toggleDirection,
  setPayment,
  setShipmentValue,
  toggleMode,
  setResolvedServices,
  toggleResolvedService,
  toggleServiceAccountCode,
  setServiceParentCodes,
} from '../../../../redux/pricingWizardSlice';
import {
  FREIGHT_DIRECTIONS,
  MODE_ROWS,
  PAYMENT_LABELS,
  SERVICE_TYPES,
  FAMILIES_BY_DIRECTION,
  derivedServicesForMode,
  resolveServicesFromProfile,
  normalizeStCode,
} from '../constants';
import SectionTitle from '../components/SectionTitle';

function OptionCard({ checked, onClick, icon: Icon, label, sub, variant = 'checkbox', accentColor }) {
  const theme = useTheme();
  const color = accentColor || theme.palette.primary.main;
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        borderColor: checked ? color : 'divider',
        borderWidth: 1,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all .15s',
        overflow: 'hidden',
        bgcolor: checked ? alpha(color, 0.06) : 'background.paper',
        '&:hover': { borderColor: color, bgcolor: alpha(color, 0.04) },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 1.5 }}>
        <Box
          sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            bgcolor: checked ? alpha(color, 0.12) : 'action.hover',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {Icon && <Icon sx={{ fontSize: 20, color: checked ? color : 'text.secondary' }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {variant === 'checkbox'
              ? <Checkbox size="small" checked={checked} sx={{ p: 0, pointerEvents: 'none' }} />
              : <Radio size="small" checked={checked} sx={{ p: 0, pointerEvents: 'none' }} />}
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: checked ? color : 'text.primary' }}>
              {label}
            </Typography>
          </Stack>
          {sub && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.3 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

export default function Stage1_Profile() {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.pricingWizard.profile);

  const { data: warehouseData, error: warehouseError } = useGetWmsWarehouseQuery();
  useEffect(() => {
    if (warehouseData) console.log('[Step 2] WMS Warehouse API response:', warehouseData);
    if (warehouseError) console.error('[Step 2] WMS Warehouse API error:', warehouseError);
  }, [warehouseData, warehouseError]);

  // Serviceability intel groups — live courier coverage data per service type
  const { data: serviceGroups = [] } = useGetServiceabilityGroupsQuery();
  // Build a lookup: service type code → group object (for O(1) access in each card).
  // Keyed on the NORMALISED code — service_type_master spells the NDD codes
  // differently from our ids (SC-NDD_FWD_REG, Intercity_NDD_REG), and an exact-key
  // map silently returned nothing for them ("No Couriers Mapped" with 0 parents).
  const groupByCode = Object.fromEntries(serviceGroups.map((g) => [normalizeStCode(g.code), g]));

  const resolved = useSelector((s) => s.pricingWizard.resolvedServices);

  // Re-derive resolved services whenever directions / modes / shipment value change,
  // while preserving services the analyst added manually (those the current
  // derivation wouldn't reproduce).
  const profileKey = JSON.stringify({ d: profile.directions, m: profile.modes, v: profile.shipment_value });
  useEffect(() => {
    const derived = resolveServicesFromProfile(profile);
    const derivedIds = new Set(derived.map((d) => d.id));
    const manualKept = resolved.filter(
      (r) => r.derivation === 'Manually added by analyst' && !derivedIds.has(r.id)
    );
    const next = [...derived, ...manualKept];
    const sameAsNow = next.length === resolved.length && next.every((n) => resolved.find((r) => r.id === n.id));
    if (!sameAsNow) dispatch(setResolvedServices(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileKey]);

  const families = FAMILIES_BY_DIRECTION(profile.directions, profile.shipment_value);

  const theme = useTheme();

  const DIRECTION_META = {
    forward:      { icon: LocalShippingOutlinedIcon,      sub: 'Deliver shipments to customers', color: theme.palette.primary.main },
    reverse_noqc: { icon: AssignmentReturnOutlinedIcon,   sub: 'Customer returns without inspection', color: theme.palette.warning.main },
    reverse_qc:   { icon: FactCheckOutlinedIcon,          sub: 'Returns with door quality check', color: theme.palette.success.main },
  };
  const PAYMENT_META = {
    PREPAID: { icon: CreditCardOutlinedIcon,     sub: 'Customer pays before dispatch' },
    COD:     { icon: MonetizationOnOutlinedIcon, sub: 'Cash collected on delivery' },
    BOTH:    { icon: SwapHorizOutlinedIcon,      sub: 'Accept prepaid & COD orders' },
  };

  return (
    <Box>
      {/* A. Freight Direction */}
      <SectionTitle icon="🧭" title="Freight Direction" badge="multi-select · required" />
      <Grid container spacing={1.5} sx={{ mt: 0.75 }}>
        {FREIGHT_DIRECTIONS.map((d) => {
          const meta = DIRECTION_META[d.key] || {};
          return (
            <Grid item xs={12} md={4} key={d.key}>
              <OptionCard
                checked={profile.directions[d.key]}
                onClick={() => dispatch(toggleDirection(d.key))}
                icon={meta.icon}
                label={d.label}
                sub={meta.sub}
                accentColor={meta.color}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* B. Payment Mode */}
      <Box sx={{ mt: 3.5 }}>
        <SectionTitle icon="💳" title="Payment Mode" badge="required" />
        <Grid container spacing={1.5} sx={{ mt: 0.75 }}>
          {Object.keys(PAYMENT_LABELS).map((p) => {
            const meta = PAYMENT_META[p] || {};
            return (
              <Grid item xs={12} md={4} key={p}>
                <OptionCard
                  variant="radio"
                  checked={profile.payment === p}
                  onClick={() => dispatch(setPayment(p))}
                  icon={meta.icon}
                  label={PAYMENT_LABELS[p]}
                  sub={meta.sub}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* B2. Shipment Profile by Value */}
      <Box sx={{ mt: 3.5 }}>
        <SectionTitle icon="💎" title="Shipment Profile by Value" badge="required" />
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1 }}>
          Classify by average shipment value (including GST). High value = average shipment price above ₹10,000; otherwise low value.
        </Typography>
        <Grid container spacing={1.5}>
          {[
            { id: 'high', icon: DiamondOutlinedIcon, label: 'High Value', sub: 'Avg shipment price > ₹10,000 (incl. GST)' },
            { id: 'low', icon: LocalShippingOutlinedIcon, label: 'Low Value', sub: 'Avg shipment price ≤ ₹10,000 (incl. GST)' },
          ].map((o) => (
            <Grid item xs={12} md={6} key={o.id}>
              <OptionCard
                variant="radio"
                checked={profile.shipment_value === o.id}
                onClick={() => dispatch(setShipmentValue(o.id))}
                icon={o.icon}
                label={o.label}
                sub={o.sub}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* C. Mode of Delivery × Size */}
      <Box sx={{ mt: 3.5 }}>
        <SectionTitle icon="🚚" title="Mode of Delivery × Size" badge="multi-select" />
        <Paper variant="outlined" sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden' }}>
          {MODE_ROWS.map((m, idx) => {
            const row = profile.modes[m.k] || { reg: false, large: false };
            const derived = derivedServicesForMode(m.k, row.reg, row.large);
            const anyChecked = row.reg || row.large;
            return (
              <Box
                key={m.k}
                sx={{
                  px: 2, py: 1.25,
                  borderBottom: idx < MODE_ROWS.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  bgcolor: anyChecked ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                  transition: 'background-color .15s',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Mode label */}
                  <Typography sx={{ fontWeight: anyChecked ? 700 : 500, fontSize: 13.5, minWidth: 160, color: anyChecked ? 'primary.dark' : 'text.primary' }}>
                    {m.lbl}
                  </Typography>

                  {/* Reg toggle */}
                  <Box
                    onClick={() => {
                      const wasOn = row.reg;
                      dispatch(toggleMode({ mode: m.k, size: 'reg' }));
                      if (wasOn) {
                        // Unchecking: remove manually-added services exclusively from this mode
                        const produced = derivedServicesForMode(m.k, true, false);
                        produced.forEach((id) => {
                          const item = resolved.find((r) => r.id === id && r.derivation === 'Manually added by analyst');
                          if (!item) return;
                          // Keep if another currently-on mode also produces it
                          const keptByOther = MODE_ROWS.some((mr) =>
                            ['reg', 'large'].some((sz) => {
                              if (mr.k === m.k && sz === 'reg') return false;
                              return !!(profile.modes[mr.k] || {})[sz] &&
                                derivedServicesForMode(mr.k, sz === 'reg', sz === 'large').includes(id);
                            })
                          );
                          if (!keptByOther) dispatch(toggleResolvedService({ id }));
                        });
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5,
                      border: 1, borderRadius: '8px', cursor: 'pointer',
                      borderColor: row.reg ? 'primary.main' : 'divider',
                      bgcolor: row.reg ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'all .15s',
                      '&:hover': { borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.05) },
                    }}
                  >
                    <Checkbox size="small" checked={row.reg} sx={{ p: 0, pointerEvents: 'none' }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: row.reg ? 'primary.dark' : 'text.secondary' }}>Regular</Typography>
                  </Box>

                  {/* Large toggle */}
                  <Box
                    onClick={() => {
                      const wasOn = row.large;
                      dispatch(toggleMode({ mode: m.k, size: 'large' }));
                      if (wasOn) {
                        const produced = derivedServicesForMode(m.k, false, true);
                        produced.forEach((id) => {
                          const item = resolved.find((r) => r.id === id && r.derivation === 'Manually added by analyst');
                          if (!item) return;
                          const keptByOther = MODE_ROWS.some((mr) =>
                            ['reg', 'large'].some((sz) => {
                              if (mr.k === m.k && sz === 'large') return false;
                              return !!(profile.modes[mr.k] || {})[sz] &&
                                derivedServicesForMode(mr.k, sz === 'reg', sz === 'large').includes(id);
                            })
                          );
                          if (!keptByOther) dispatch(toggleResolvedService({ id }));
                        });
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5,
                      border: 1, borderRadius: '8px', cursor: 'pointer',
                      borderColor: row.large ? 'primary.main' : 'divider',
                      bgcolor: row.large ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'all .15s',
                      '&:hover': { borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.05) },
                    }}
                  >
                    <Checkbox size="small" checked={row.large} sx={{ p: 0, pointerEvents: 'none' }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: row.large ? 'primary.dark' : 'text.secondary' }}>Large</Typography>
                  </Box>

                  {/* Resulting service type chips */}
                  {derived.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ ml: 1 }}>
                      {derived.map((code) => (
                        <Chip key={code} label={code} size="small"
                          sx={{ height: 18, fontSize: 9.5, fontFamily: 'monospace', fontWeight: 600, bgcolor: '#ede9fe', color: '#5b21b6' }} />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {/* D. Service Types */}
      <Box sx={{ mt: 3.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <SectionTitle icon="🧩" title="Service Types" badge="aligned with freight direction" />
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip size="small" label="● Profile-resolved" sx={{ height: 20, fontSize: 10.5, bgcolor: '#dbeafe', color: '#1e40af' }} />
            <Chip size="small" label="○ Available" sx={{ height: 20, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Stack>
        </Stack>

      {families.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2, borderStyle: 'dashed' }}>
          <Typography variant="body2" color="text.secondary">Select at least one Freight Direction above to see service-type options.</Typography>
        </Box>
      ) : (
        families.map((fam) => {
          const list = SERVICE_TYPES
            .filter((s) => s.family === fam)
            .filter((s) => {
              // Use API direction when available, fall back to ID pattern
              const grp = groupByCode[normalizeStCode(s.id)];
              const dir = grp?.direction
                || ((s.id.startsWith('REV_') || s.id.toUpperCase().includes('REVERSE')) ? 'RVP' : 'FORWARD');

              if (dir === 'FORWARD') return !!profile.directions.forward;
              if (dir === 'RVP') {
                const noQc = s.id.includes('NOQC');
                const withQc = s.id.includes('_QC') && !s.id.includes('NOQC');
                if (noQc) return !!profile.directions.reverse_noqc;
                if (withQc) return !!profile.directions.reverse_qc;
                // HV_REVERSE and similar — show if any reverse direction is on
                return !!(profile.directions.reverse_noqc || profile.directions.reverse_qc);
              }
              return true;
            });
          const selectedCount = list.filter((s) => resolved.find((r) => r.id === s.id)).length;
          return (
            <Box key={fam} sx={{ mt: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                  {fam}
                </Typography>
                <Chip size="small" label={`${selectedCount}/${list.length} selected`}
                  color={selectedCount > 0 ? 'primary' : 'default'} variant="outlined"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              </Stack>
              <Grid container spacing={1.5}>
                {list.map((s) => {
                  const resolvedItem = resolved.find((r) => r.id === s.id);
                  return (
                    <Grid item xs={12} md={6} key={s.id}>
                      <ServiceTypeCard
                        service={s}
                        resolvedItem={resolvedItem}
                        paymentMode={profile.payment}
                        group={groupByCode[normalizeStCode(s.id)] || null}
                        onToggle={() => {
                          if (!resolvedItem) {
                            // ── Checking a service: add to resolved AND turn on producing mode ──
                            dispatch(toggleResolvedService({
                              ...s,
                              included: true,
                              derivation: 'Manually added by analyst',
                            }));
                            // Turn on every mode+size that produces this service (if not already on)
                            MODE_ROWS.forEach((mr) => {
                              ['reg', 'large'].forEach((sz) => {
                                if ((profile.modes[mr.k] || {})[sz]) return; // already on
                                if (derivedServicesForMode(mr.k, sz === 'reg', sz === 'large').includes(s.id)) {
                                  dispatch(toggleMode({ mode: mr.k, size: sz }));
                                }
                              });
                            });
                          } else {
                            // ── Unchecking a service: remove from resolved AND uncheck orphaned modes ──
                            dispatch(toggleResolvedService(s));
                            const afterRemove = resolved.filter((r) => r.id !== s.id);
                            MODE_ROWS.forEach((mr) => {
                              ['reg', 'large'].forEach((sz) => {
                                const modeRow = profile.modes[mr.k] || { reg: false, large: false };
                                if (!modeRow[sz]) return;
                                const derived = derivedServicesForMode(mr.k, sz === 'reg', sz === 'large');
                                if (!derived.includes(s.id)) return;
                                const anyStillSelected = derived.some((id) => afterRemove.find((r) => r.id === id));
                                if (!anyStillSelected) dispatch(toggleMode({ mode: mr.k, size: sz }));
                              });
                            });
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })
      )}
      </Box>

      {/* Conflict warnings */}
      <ProfileConflictWarnings profile={profile} resolved={resolved} />
      {/* Pickup / Warehouse moved to the Sign-up step (FTR team) — see Stage0_Signup. */}
    </Box>
  );
}

// v6.3.3 — Service Type card with the parent/account-code coverage drill-down
// rendered INLINE (only when the card is selected). Replaces the standalone
// "Courier coverage" section. The checkbox toggle and the drill toggle are
// separate click targets so expanding the drill never flips the selection.
//
// v6.4 — the drill-down is now an interactive multi-select: pick a parent
// courier, then tick the specific account codes under it. The selection is
// stored per service type in wizard.accountCodeSelection and scopes which codes
// flow into the base-pricing upload template. An untouched service defaults to
// "all codes selected" (backward compatible).
function ServiceTypeCard({ service, resolvedItem, paymentMode, onToggle, group }) {
  const dispatch = useDispatch();
  const [drillOpen, setDrillOpen] = useState(false);
  const checked = !!resolvedItem;

  const linkedCodes = group?.linkedCodes?.filter((c) => c.isActive !== false) ?? [];
  const parentMap = linkedCodes.reduce((acc, lc) => {
    const p = lc.parent || lc.cpAccountCode;
    if (!acc[p]) acc[p] = [];
    acc[p].push(lc);
    return acc;
  }, {});
  const parents = Object.keys(parentMap).sort();
  const totalCodes = linkedCodes.length;

  // Per-service account-code selection. Absent => all codes selected (default).
  const allCodeStrs = linkedCodes.map((lc) => lc.cpAccountCode);
  const selection = useSelector((s) => s.pricingWizard.accountCodeSelection[service.id]);
  const selectedSet = selection ? new Set(selection) : new Set(allCodeStrs);
  const selectedCount = allCodeStrs.filter((c) => selectedSet.has(c)).length;
  const allSelected = totalCodes > 0 && selectedCount === totalCodes;

  const toggleCode = (code) =>
    dispatch(toggleServiceAccountCode({ serviceId: service.id, code, allCodes: allCodeStrs }));
  const toggleParent = (parentCodes, select) =>
    dispatch(setServiceParentCodes({ serviceId: service.id, parentCodes, allCodes: allCodeStrs, select }));

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? 'primary.lighter' : 'background.paper',
        transition: 'all .15s',
      }}
    >
      {/* Toggle area */}
      <CardActionArea onClick={onToggle} sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Checkbox checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Chip
                label={service.id}
                size="small"
                sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, height: 20, bgcolor: 'grey.100', color: 'text.primary' }}
              />
              <Stack direction="row" spacing={0.5}>
                {checked && <Chip label="From profile" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#dbeafe', color: '#1e40af' }} />}
                {/* Couriers-mapped signal (moved here from the removed "commercial construct"
                    section): a selected service with zero mapped couriers blocks the rate card. */}
                {checked && (
                  <Chip
                    size="small"
                    label={totalCodes > 0 ? `${totalCodes} couriers mapped` : 'No couriers mapped'}
                    sx={totalCodes > 0
                      ? { height: 18, fontSize: 10, bgcolor: '#dcfce7', color: '#166534' }
                      : { height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }}
                  />
                )}
                {checked && totalCodes === 0 && (
                  <Chip
                    size="small"
                    label="Rate card blocked"
                    sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' }}
                  />
                )}
              </Stack>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              {service.sub}
            </Typography>
            {resolvedItem?.derivation && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.75, display: 'block', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                ↳ {resolvedItem.derivation}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardActionArea>

      {/* Inline drill-down — only when selected */}
      {checked && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'background.neutral' } }}
            onClick={() => setDrillOpen((v) => !v)}
          >
            <IconButton size="small" sx={{ p: 0.25, transform: drillOpen ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
            <Chip
              label={`${parents.length} parent${parents.length !== 1 ? 's' : ''}`}
              size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label={`${selectedCount}/${totalCodes} code${totalCodes !== 1 ? 's' : ''} selected`}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: selectedCount === 0 ? '#fee2e2' : allSelected ? '#ede9fe' : '#fef3c7',
                color: selectedCount === 0 ? '#991b1b' : allSelected ? '#5b21b6' : '#92400e',
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
              {drillOpen ? 'click to collapse' : 'pick codes for upload'}
            </Typography>
          </Stack>
          <Collapse in={drillOpen}>
            <Box sx={{ p: 1.5, pt: 0.5, bgcolor: 'background.neutral' }}>
              {parents.length === 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No courier partners mapped to this service type yet.
                </Typography>
              )}
              {parents.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Select a parent courier and tick the account codes to push into the base-pricing
                    upload template. Codes left unticked are excluded from this merchant's pricing upload.
                  </Typography>
                  <Stack spacing={1.25}>
                    {parents.map((parent) => {
                      const codes = parentMap[parent];
                      const parentCodeStrs = codes.map((lc) => lc.cpAccountCode);
                      const selCount = parentCodeStrs.filter((c) => selectedSet.has(c)).length;
                      const allOn = selCount === parentCodeStrs.length;
                      const someOn = selCount > 0 && !allOn;
                      return (
                        <Box key={parent} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1, bgcolor: 'background.paper' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleParent(parentCodeStrs, !allOn)}
                          >
                            <Checkbox
                              size="small"
                              checked={allOn}
                              indeterminate={someOn}
                              sx={{ p: 0.25, pointerEvents: 'none' }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {parent}
                            </Typography>
                            <Chip
                              label={`${selCount}/${parentCodeStrs.length}`}
                              size="small"
                              sx={{ ml: 0.5, height: 16, fontSize: 9.5, fontWeight: 700 }}
                            />
                          </Stack>
                          <Box sx={{ mt: 0.5, pl: 3.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
                            {codes.map((lc) => {
                              const on = selectedSet.has(lc.cpAccountCode);
                              return (
                                <Stack
                                  key={lc.cpAccountCode}
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                  onClick={() => toggleCode(lc.cpAccountCode)}
                                  sx={{ cursor: 'pointer', py: 0.25, px: 0.5, borderRadius: 1, minWidth: 0, '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                  <Checkbox size="small" checked={on} sx={{ p: 0.25, pointerEvents: 'none', flexShrink: 0 }} />
                                  <Typography
                                    sx={{
                                      fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5,
                                      color: on ? '#166534' : 'text.disabled', fontWeight: on ? 600 : 400,
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {lc.cpAccountCode}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              )}

            </Box>
          </Collapse>
        </Box>
      )}
    </Card>
  );
}

function ProfileConflictWarnings({ profile, resolved }) {
  const conflicts = [];
  const hasReverseDir = profile.directions.reverse_noqc || profile.directions.reverse_qc;
  const hasReverseSvc = resolved.some((r) => r.family === 'Reverse');
  if (profile.directions.reverse_qc && !resolved.some((r) => r.id === 'REV_REG_QC' || r.id === 'REV_LARGE_QC')) {
    conflicts.push('Reverse with QC declared in freight direction, but no QC-based reverse service type is selected.');
  }
  if (
    profile.directions.reverse_noqc &&
    !resolved.some((r) => r.id === 'REV_REG_NOQC' || r.id === 'REV_LARGE_NOQC')
  ) {
    conflicts.push('Reverse without QC declared in freight direction, but no non-QC reverse service type is selected.');
  }
  if (hasReverseSvc && !hasReverseDir) {
    conflicts.push('Reverse service types are selected, but neither Reverse direction is enabled in Freight Direction.');
  }
  if (
    profile.directions.forward &&
    !resolved.some((r) => /^FWD_|^SDD_|^SC_NDD|^INTERCITY|^H2TO4$|^SUB2H$|^HV_FWD/.test(r.id))
  ) {
    conflicts.push('Forward direction is selected, but no forward-family service type is selected.');
  }
  if (conflicts.length === 0) return null;
  return (
    <Alert severity="warning" sx={{ mt: 2 }} icon={<span>⚠️</span>}>
      <b>Profile ↔ Service Type inconsistency</b> (warn-only · you can continue):
      <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
        {conflicts.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </Box>
    </Alert>
  );
}

function LegendDot({ color, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: color, border: 1, borderColor: 'divider' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
}
